import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import toast from "react-hot-toast";
import axiosInstance from "../../../axiosInstance";
import Navbar from "../../components/Navbar";
import Sidebar from "../../components/Sidebar";
import { getJobPortalAuthConfig } from "../../../helpers/jobPortalAuth";
import { jobStatusUpdateOptions, recruiterJobStatusOptions } from "../../../helpers/jobPortalMeta";

const STATUS_STYLES = {
  active: { bg: "#e6f4ed", color: "#198754", dot: "#198754" },
  closed: { bg: "#f1f3f5", color: "#6c757d", dot: "#6c757d" },
  draft: { bg: "#fff3e0", color: "#fd7e14", dot: "#fd7e14" },
  paused: { bg: "#e7f1ff", color: "#0d6efd", dot: "#0d6efd" },
};

const WORK_MODE_LABELS = { remote: "🏠 Remote", onsite: "🏢 Onsite", hybrid: "🔀 Hybrid" };
const EMP_LABELS = { full_time: "Full Time", part_time: "Part Time", contract: "Contract", internship: "Internship", freelance: "Freelance" };

const SkeletonJobRow = () => (
  <div className="rjob-card placeholder-glow p-4">
    <div className="d-flex gap-3 align-items-center">
      <span className="placeholder rounded-3" style={{ width: 48, height: 48 }} />
      <div className="flex-grow-1">
        <span className="placeholder col-5 rounded d-block mb-2" />
        <span className="placeholder col-4 rounded d-block mb-2" />
        <span className="placeholder col-3 rounded d-block" style={{ height: 20 }} />
      </div>
      <div className="d-flex gap-2">
        <span className="placeholder rounded" style={{ width: 70, height: 32 }} />
        <span className="placeholder rounded" style={{ width: 70, height: 32 }} />
      </div>
    </div>
  </div>
);

const RecruiterJobs = () => {
  const [loading, setLoading] = useState(true);
  const [jobs, setJobs] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [subscription, setSubscription] = useState(null);

  const fetchJobs = async () => {
    try {
      setLoading(true);
      const authConfig = await getJobPortalAuthConfig();
      const params = new URLSearchParams();
      params.set("limit", "100");
      if (searchTerm) params.set("search", searchTerm);
      if (statusFilter) params.set("status", statusFilter);
      const response = await axiosInstance.get(`/job-portal/recruiter/jobs?${params.toString()}`, authConfig);
      setJobs(response.data?.data?.jobs || []);
    } catch (error) {
      toast.error(error.response?.data?.message || "Unable to load jobs");
    } finally {
      setLoading(false);
    }
  };

  const fetchSubscription = async () => {
    try {
      const authConfig = await getJobPortalAuthConfig();
      const response = await axiosInstance.get("/job-portal/subscription/me", authConfig);
      setSubscription(response.data?.data?.subscription || null);
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    fetchJobs();
    fetchSubscription();
  }, [statusFilter]);

  const filteredJobs = useMemo(() => jobs, [jobs]);

  const counts = useMemo(() => ({
    total: jobs.length,
    active: jobs.filter((j) => j.jobStatus === "active").length,
    draft: jobs.filter((j) => j.jobStatus === "draft").length,
    closed: jobs.filter((j) => j.jobStatus === "closed").length,
  }), [jobs]);

  const updateJobStatus = async (jobId, nextStatus) => {
    try {
      const authConfig = await getJobPortalAuthConfig();
      await axiosInstance.put(`/job-portal/jobs/${jobId}`, { jobStatus: nextStatus, status: nextStatus === "active" ? 1 : 0 }, authConfig);
      toast.success("Job updated");
      fetchJobs();
    } catch (error) {
      toast.error(error.response?.data?.message || "Unable to update job");
    }
  };

  const deleteJob = async (jobId) => {
    if (!window.confirm("Delete this job? This will also remove linked applications.")) return;
    try {
      const authConfig = await getJobPortalAuthConfig();
      await axiosInstance.delete(`/job-portal/jobs/${jobId}`, authConfig);
      toast.success("Job deleted");
      fetchJobs();
    } catch (error) {
      toast.error(error.response?.data?.message || "Unable to delete job");
    }
  };

  return (
    <>
      <style>{`
        .rjob-card {
          border: 1.5px solid #e9ecef;
          border-radius: 14px;
          background: #fff;
          margin-bottom: 10px;
          padding: 18px 20px;
          transition: box-shadow 0.18s, border-color 0.18s;
        }
        .rjob-card:hover { box-shadow: 0 4px 18px rgba(0,0,0,0.08); border-color: #dee2e6; }
        .rjob-initials {
          width: 48px; height: 48px; border-radius: 12px;
          display: flex; align-items: center; justify-content: center;
          font-size: 15px; font-weight: 700; flex-shrink: 0;
        }
        .status-pill {
          display: inline-flex; align-items: center; gap: 5px;
          border-radius: 20px; font-size: 11.5px; font-weight: 600;
          padding: 4px 12px; text-transform: capitalize;
        }
        .status-dot { width: 6px; height: 6px; border-radius: 50%; display: inline-block; }
        .action-btn { border-radius: 8px; font-size: 12.5px; font-weight: 500; }
        .filter-input, .filter-select {
          border: 1.5px solid #e9ecef; border-radius: 10px;
          padding: 9px 14px; font-size: 13.5px; width: 100%;
          background: #f8f9fa; outline: none; transition: border-color 0.15s;
        }
        .filter-input:focus, .filter-select:focus {
          border-color: #0d6efd; background: #fff;
          box-shadow: 0 0 0 3px rgba(13,110,253,0.10);
        }
        .filter-select {
          appearance: none; cursor: pointer; padding-right: 32px;
          background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 16 16'%3E%3Cpath fill='%23adb5bd' d='M7.247 11.14L2.451 5.658C1.885 5.013 2.345 4 3.204 4h9.592a1 1 0 0 1 .753 1.659l-4.796 5.48a1 1 0 0 1-1.506 0z'/%3E%3C/svg%3E");
          background-repeat: no-repeat; background-position: right 12px center;
        }
        .stat-chip {
          display: inline-flex; align-items: center; gap: 5px;
          background: #f8f9fa; border-radius: 8px;
          padding: 5px 12px; font-size: 12.5px; font-weight: 600; color: #495057;
        }
        .inline-status-select {
          border: 1.5px solid #e9ecef; border-radius: 8px;
          padding: 5px 10px; font-size: 12px; background: #fff;
          cursor: pointer; outline: none; transition: border-color 0.15s;
          min-width: 130px;
        }
        .inline-status-select:focus { border-color: #0d6efd; }
      `}</style>

      <Sidebar />
      <main className="content">
        <Navbar />

        {/* Header */}
        <div className="py-4">
          <div className="d-flex justify-content-between align-items-center flex-wrap gap-3">
            <div>
              <h4 className="fw-bold mb-1" style={{ letterSpacing: "-0.3px" }}>
                <i className="bi bi-briefcase-fill text-primary me-2" />My Jobs
              </h4>
              <p className="text-muted mb-0 small">Manage your job postings and applicant flow.</p>
            </div>
            <div className="d-flex gap-2">
              <Link to="/recruiter/plans" className="btn btn-outline-dark action-btn">
                <i className="bi bi-credit-card me-2" />My Plan
              </Link>
              <Link
                to={subscription?.status === "active" ? "/recruiter/jobs/new" : "/recruiter/plans"}
                className={`btn ${subscription?.status === "active" ? "btn-primary" : "btn-secondary"} action-btn`}
              >
                <i className="bi bi-plus-lg me-2" />
                {subscription?.status === "active" ? "Post a Job" : "Buy Plan to Post"}
              </Link>
            </div>
          </div>
        </div>

        {subscription?.status !== "active" && (
          <div className="alert alert-warning border-0 shadow-sm">
            An active recruiter plan is required to post jobs. Please purchase a plan to continue.
          </div>
        )}

        {/* Quick Stats */}
        {!loading && (
          <div className="d-flex flex-wrap gap-2 mb-4">
            <span className="stat-chip"><i className="bi bi-grid text-muted" />{counts.total} Total</span>
            <span className="stat-chip"><span className="status-dot" style={{ background: "#198754" }} />{counts.active} Active</span>
            <span className="stat-chip"><span className="status-dot" style={{ background: "#fd7e14" }} />{counts.draft} Draft</span>
            <span className="stat-chip"><span className="status-dot" style={{ background: "#6c757d" }} />{counts.closed} Closed</span>
          </div>
        )}

        {/* Search & Filter Toolbar */}
        <div className="row g-3 mb-4">
          <div className="col-md-7">
            <div style={{ position: "relative" }}>
              <i className="bi bi-search" style={{ position: "absolute", left: 13, top: "50%", transform: "translateY(-50%)", color: "#adb5bd", fontSize: 14, pointerEvents: "none" }} />
              <input
                className="filter-input"
                style={{ paddingLeft: 36 }}
                placeholder="Search by title, role, location…"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && fetchJobs()}
              />
            </div>
          </div>
          <div className="col-md-3">
            <select className="filter-select" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
              {recruiterJobStatusOptions.map((o) => (
                <option key={o.value || "all"} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>
          <div className="col-md-2">
            <button
              className="btn btn-dark w-100 action-btn text-white"
              style={{ background: "linear-gradient(135deg,#212529,#343a40)", border: "none" }}
              onClick={fetchJobs}
            >
              <i className="bi bi-search me-1" />Search
            </button>
          </div>
        </div>

        {/* Job List */}
        {loading ? (
          <div>{Array.from({ length: 4 }).map((_, i) => <SkeletonJobRow key={i} />)}</div>
        ) : filteredJobs.length === 0 ? (
          <div className="card border-0 shadow-sm" style={{ borderRadius: 16 }}>
            <div className="card-body text-center py-5">
              <i className="bi bi-briefcase text-muted" style={{ fontSize: 48, opacity: 0.25 }} />
              <p className="text-muted mt-3 mb-3 small">No jobs match the current filters.</p>
              <Link to="/recruiter/jobs/new" className="btn btn-primary btn-sm action-btn">
                <i className="bi bi-plus me-1" />Post your first job
              </Link>
            </div>
          </div>
        ) : (
          filteredJobs.map((job) => {
            const statusStyle = STATUS_STYLES[job.jobStatus] || STATUS_STYLES.active;
            const initials = (job.company?.name || "CO").split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase();
            const avatarColors = [["#e7f1ff","#0d6efd"],["#f0ebff","#6f42c1"],["#e6f4ed","#198754"],["#fff3e0","#fd7e14"],["#fce8f3","#c2185b"]];
            const [abg, afg] = avatarColors[(job.title?.charCodeAt(0) || 0) % avatarColors.length];

            return (
              <div className="rjob-card" key={job.id}>
                <div className="d-flex justify-content-between align-items-center gap-3 flex-wrap">
                  {/* Left: Job Info */}
                  <div className="d-flex gap-3 align-items-center">
                    <div className="rjob-initials" style={{ background: abg, color: afg }}>{initials}</div>
                    <div>
                      <div className="fw-semibold" style={{ fontSize: 15 }}>{job.title}</div>
                      <div className="text-muted" style={{ fontSize: 12, marginTop: 2 }}>
                        <i className="bi bi-building me-1" />{job.company?.name || "Company"}
                        <span className="mx-2">·</span>
                        <i className="bi bi-geo-alt me-1" />{job.location?.display || "Location TBD"}
                      </div>
                      <div className="d-flex align-items-center gap-2 mt-2 flex-wrap">
                        <span className="status-pill" style={{ background: statusStyle.bg, color: statusStyle.color }}>
                          <span className="status-dot" style={{ background: statusStyle.dot }} />
                          {job.jobStatus || "active"}
                        </span>
                        <span className="text-muted" style={{ fontSize: 11 }}>
                          <i className="bi bi-people me-1" />{job.applicationCount || 0} applicants
                        </span>
                        {job.employmentType && (
                          <span className="text-muted" style={{ fontSize: 11 }}>
                            <i className="bi bi-clock me-1" />{EMP_LABELS[job.employmentType] || job.employmentType}
                          </span>
                        )}
                        {job.workMode && (
                          <span className="text-muted" style={{ fontSize: 11 }}>
                            {WORK_MODE_LABELS[job.workMode] || job.workMode}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Right: Actions */}
                  <div className="d-flex gap-2 align-items-center flex-wrap">
                    <select
                      className="inline-status-select"
                      value={job.jobStatus || "active"}
                      onChange={(e) => updateJobStatus(job.id, e.target.value)}
                    >
                      {jobStatusUpdateOptions.map((o) => (
                        <option key={o.value} value={o.value}>{o.label}</option>
                      ))}
                    </select>
                    <Link to={`/recruiter/jobs/${job.id}/edit`} className="btn btn-sm btn-outline-primary action-btn">
                      <i className="bi bi-pencil me-1" />Edit
                    </Link>
                    <Link to={`/recruiter/jobs/${job.id}/applicants`} className="btn btn-sm btn-outline-dark action-btn">
                      <i className="bi bi-people me-1" />Applicants
                    </Link>
                    <button className="btn btn-sm btn-outline-danger action-btn" onClick={() => deleteJob(job.id)}>
                      <i className="bi bi-trash" />
                    </button>
                  </div>
                </div>
              </div>
            );
          })
        )}
      </main>
    </>
  );
};

export default RecruiterJobs;
